import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
/**
 * Attempts to resolve the active schoolId from browser storage.
 * Provides a single place to update future storage keys without touching every caller.
 */
export function resolveSchoolId(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const possibleKeys = ['school_id', 'schoolId', 'currentSchoolId', 'currentSchool']

  for (const key of possibleKeys) {
    const value = window.localStorage.getItem(key)
    if (value && value !== 'undefined' && value !== 'null') {
      return value
    }
  }

  const rawUser = window.localStorage.getItem('user')
  if (rawUser) {
    try {
      const parsed = JSON.parse(rawUser)
      return (
        parsed?.schoolId ||
        parsed?.school?.id ||
        parsed?.currentSchoolId ||
        null
      )
    } catch (error) {
      console.warn('Failed to parse stored user for school resolution', error)
    }
  }

  return null
}

// CSV Generation Utility
export function generateCSV<T extends Record<string, any>>(data: T[]): string {
  if (!data || data.length === 0) return '';
  
  // Get headers from first object
  const headers = Object.keys(data[0]);
  
  // Create CSV header row
  const csvHeader = headers.map(header => `"${header}"`).join(',');
  
  // Create CSV data rows
  const csvRows = data.map(row => 
    headers.map(header => {
      const value = row[header];
      // Handle null, undefined, and objects
      if (value === null || value === undefined) return '""';
      if (typeof value === 'object') return `"${JSON.stringify(value)}"`;
      // Escape quotes in string values
      const stringValue = String(value);
      return `"${stringValue.replace(/"/g, '""')}"`;
    }).join(',')
  );
  
  // Combine header and rows
  return [csvHeader, ...csvRows].join('\n');
}

// Price formatting utility with fallback support
export function formatPrice(price: number, currency?: string, locale = 'en-IN'): string {
  try {
    const validCurrency = currency && currency.trim() ? currency : 'INR';
    
    // Check if Intl.NumberFormat is available
    if (typeof Intl !== 'undefined' && Intl.NumberFormat) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: validCurrency,
      }).format(price);
    }
    
    // Fallback for environments without Intl support
    const currencySymbol = validCurrency === 'INR' ? '₹' : 
                         validCurrency === 'USD' ? '$' : 
                         validCurrency === 'EUR' ? '€' : 
                         validCurrency;
    
    return `${currency} ${price.toLocaleString(locale)}`;
  } catch (error) {
    console.warn('Error formatting price:', error);
    // Ultimate fallback
    return `${currency || 'INR'} ${price}`;
  }
}

// Currency formatting utility with fallback support
export function formatCurrency(amount: number, currency = 'USD', locale = 'en-US'): string {
  try {
    // Check if Intl.NumberFormat is available
    if (typeof Intl !== 'undefined' && Intl.NumberFormat) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(amount);
    }
    
    // Fallback for environments without Intl support
    const currencySymbol = currency === 'USD' ? '$' : 
                         currency === 'INR' ? '₹' : 
                         currency === 'EUR' ? '€' : 
                         currency;
    
    return `${currencySymbol} ${amount.toLocaleString(locale)}`;
  } catch (error) {
    console.warn('Error formatting currency:', error);
    // Ultimate fallback
    return `${currency} ${amount}`;
  }
}

/**
 * Helper to get clean, effective non-empty options for Select inputs.
 * For true_false questions, if fewer than 2 valid options exist, fallbacks to ['True', 'False'].
 */
export function getEffectiveOptions(
  options?: Array<string | { value: string }> | null,
  questionType?: string | null
): string[] {
  const stripTags = (str: string) => String(str || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  const raw = (options || []).map(opt => typeof opt === 'string' ? opt : (opt?.value || ''));
  const valid = raw.filter(opt => stripTags(opt) !== '');

  if (questionType === 'true_false') {
    if (valid.length < 2) {
      return ['True', 'False'];
    }
  }

  return valid.length > 0 ? valid : (questionType === 'true_false' ? ['True', 'False'] : raw);
}

/**
 * Helper to match correct answer value to one of the options for Select inputs.
 * Handles cases where correctAnswer is an option text, an option letter ('A','B','C','D'),
 * an option index ('1','2','3','4'), or contains HTML/whitespace/case differences.
 */
export function getCorrectAnswerSelectValue(
  options: Array<string | { value: string }>,
  correctAnswer?: string | null,
  correctAnswerLetter?: string | null,
  questionType?: string | null
): string | undefined {
  const opts = getEffectiveOptions(options, questionType);
  if (opts.length === 0) return undefined;

  const stripTags = (str: string) => String(str || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

  const ans = (correctAnswer || '').trim();
  const letter = (correctAnswerLetter || '').trim();
  const strippedAns = stripTags(ans);

  // 1. Direct match with exact option value or stripped text
  if (ans) {
    const directMatch = opts.find(o => o === ans || o.trim() === ans || stripTags(o).toLowerCase() === strippedAns.toLowerCase());
    if (directMatch) return directMatch;
  }

  // 2. Special handling for boolean true/false or "True"/"False" text variations
  const lowerAns = strippedAns.toLowerCase();
  if (lowerAns === 'true' || lowerAns === 'false' || lowerAns === 't' || lowerAns === 'f') {
    const targetText = (lowerAns === 'true' || lowerAns === 't') ? 'true' : 'false';
    const tfMatch = opts.find(o => {
      const strippedO = stripTags(o).toLowerCase();
      return strippedO === targetText || (targetText === 'true' && strippedO.startsWith('true')) || (targetText === 'false' && strippedO.startsWith('false'));
    });
    if (tfMatch) return tfMatch;
  }

  // 3. Letter match from correctAnswerLetter or correctAnswer (e.g. "A", "B", "C", "D", "Option A", "A)", "A.")
  const targetToken = (letter || ans).toUpperCase();
  const letterMatch = targetToken.match(/^(?:OPTION\s+)?([A-Z])[\.\)]?$/i);
  if (letterMatch) {
    const index = letterMatch[1].toUpperCase().charCodeAt(0) - 65;
    if (index >= 0 && index < opts.length) {
      return opts[index];
    }
  }

  // 4. Numeric match ("1", "2", "3", "4", "Option 1")
  const numMatch = targetToken.match(/^(?:OPTION\s+)?([1-9])[\.\)]?$/i);
  if (numMatch) {
    const index = parseInt(numMatch[1], 10) - 1;
    if (index >= 0 && index < opts.length) {
      return opts[index];
    }
  }

  // 5. Fallback: loose text match within option strings
  if (ans) {
    const looseMatch = opts.find(o => {
      const s = stripTags(o).toLowerCase();
      return s && (s.includes(lowerAns) || lowerAns.includes(s));
    });
    if (looseMatch) return looseMatch;
  }

  return ans || undefined;
}

/**
 * Helper to check if an option is selected for multiple_choice questions.
 * Robustly checks correctAnswers array, correctAnswerLetter string, and correctAnswer string.
 */
export function isOptionCheckedForMultipleChoice(
  optionValue: string,
  optionIndex: number,
  correctAnswers?: string[] | null,
  correctAnswerLetter?: string | null,
  correctAnswer?: string | null
): boolean {
  const letter = String.fromCharCode(65 + optionIndex);
  const stripTags = (str: string) => String(str || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  const targetVal = stripTags(optionValue).toLowerCase();

  // 1. Check correctAnswers array
  if (Array.isArray(correctAnswers) && correctAnswers.length > 0) {
    const found = correctAnswers.some(ans => {
      const rawAns = String(ans || '').trim();
      if (!rawAns) return false;
      if (rawAns === optionValue || rawAns.toUpperCase() === letter) return true;
      if (targetVal && stripTags(rawAns).toLowerCase() === targetVal) return true;
      const letterMatch = rawAns.match(/^(?:OPTION\s+)?([A-Z])[\.\)]?$/i);
      if (letterMatch && letterMatch[1].toUpperCase() === letter) return true;
      return false;
    });
    if (found) return true;
  }

  // 2. Check correctAnswerLetter string (e.g. "A,B" or "A, B")
  if (correctAnswerLetter) {
    const letters = String(correctAnswerLetter).split(',').map(l => l.trim().toUpperCase());
    if (letters.includes(letter)) return true;
  }

  // 3. Check correctAnswer string (e.g. "Paris | London" or "A | B")
  if (correctAnswer) {
    const parts = String(correctAnswer).split(/\||,/).map(p => p.trim());
    const foundInAnswer = parts.some(p => {
      if (p === optionValue || p.toUpperCase() === letter) return true;
      if (targetVal && stripTags(p).toLowerCase() === targetVal) return true;
      const letterMatch = p.match(/^(?:OPTION\s+)?([A-Z])[\.\)]?$/i);
      if (letterMatch && letterMatch[1].toUpperCase() === letter) return true;
      return false;
    });
    if (foundInAnswer) return true;
  }

  return false;
}

/**
 * Helper to get an array of correct answers for multiple_choice, fill_blank, or multiple_blanks.
 */
export function getCorrectAnswersArray(
  correctAnswers?: string[] | null,
  correctAnswer?: string | null
): string[] {
  if (Array.isArray(correctAnswers) && correctAnswers.length > 0) {
    return correctAnswers;
  }
  if (correctAnswer && typeof correctAnswer === 'string' && correctAnswer.trim()) {
    return correctAnswer.split(/\||,/).map(s => s.trim()).filter(Boolean);
  }
  return [];
}
